import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Package, Plus, Pencil, Trash2, Copy, Users, ChevronDown, ChevronUp,
  Briefcase, Stethoscope, FileText, Shield, ClipboardCheck, X, Search,
  Paperclip, Upload, PenTool, Settings2, Download,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_COLORS = {
  'New Hire': 'bg-blue-100 text-blue-700',
  'HR': 'bg-purple-100 text-purple-700',
  'Manager': 'bg-amber-100 text-amber-700',
  'IT': 'bg-emerald-100 text-emerald-700',
};

const INDUSTRY_ICONS = {
  'Home Health': Stethoscope,
  'default': Briefcase,
};

const ITEM_TYPE_ICONS = {
  'document': FileText,
  'task': ClipboardCheck,
  'signature': FileText,
  'acknowledgment': Shield,
};

function PackItemRow({ item, onUpdate, onRemove, readOnly, esignTemplates = [], credentialTypes = [], headers }) {
  const TypeIcon = ITEM_TYPE_ICONS[item.item_type] || ClipboardCheck;
  const roleColor = ROLE_COLORS[item.assignee_role] || 'bg-gray-100 text-gray-700';
  const [expanded, setExpanded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);

  const templateName = esignTemplates.find(t => t.id === item.esign_template_id)?.name;

  if (readOnly) {
    return (
      <div className="py-2 px-3 rounded-md border border-border/50 text-sm" data-testid={`pack-item-${item.name}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <TypeIcon size={14} className="text-muted-foreground shrink-0" />
            <span className="truncate font-medium">{item.name}</span>
            {item.required && <Badge className="text-[10px] px-1.5 py-0 bg-red-50 text-red-600 shrink-0">Required</Badge>}
          </div>
          <Badge className={`text-[10px] px-2 py-0.5 shrink-0 ml-2 ${roleColor}`}>{item.assignee_role}</Badge>
        </div>
        {item.description && <p className="text-xs text-muted-foreground mt-1 ml-6">{item.description}</p>}
        <div className="flex flex-wrap gap-2 mt-1 ml-6">
          {item.item_type === 'signature' && templateName && (
            <span className="inline-flex items-center gap-1 text-[11px] text-indigo-600"><PenTool size={11} /> Signs: {templateName}</span>
          )}
          {item.item_type === 'document' && item.credential_type && (
            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600"><Upload size={11} /> Files as: {item.credential_type}</span>
          )}
          {item.attachment?.file_name && (
            <span className="inline-flex items-center gap-1 text-[11px] text-blue-600"><Paperclip size={11} /> {item.attachment.file_name}</span>
          )}
          {item.due_mode && item.due_mode !== 'none' && (
            <span className="inline-flex items-center gap-1 text-[11px] text-orange-600">
              <ClipboardCheck size={11} /> Due: {item.due_mode === 'fixed' ? item.due_date
                : item.due_mode === 'before_hire' ? `${item.due_offset_days ?? 0}d before hire`
                : `${item.due_offset_days ?? 0}d after hire`}
              {item.reminder_days_before != null && ` · reminder ${item.reminder_days_before}d before`}
            </span>
          )}
        </div>
      </div>
    );
  }

  const uploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/onboarding/attachments`, fd, { headers });
      onUpdate({ ...item, attachment: res.data });
      toast.success('Reference file attached');
    } catch { toast.error('Upload failed'); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="border rounded-md p-2 bg-background" data-testid={`edit-pack-item-${item.name}`}>
      <div className="flex items-center gap-2">
        <Input value={item.name} onChange={(e) => onUpdate({ ...item, name: e.target.value })} placeholder="Task name" className="flex-1 h-8 text-sm" />
        <select value={item.item_type} onChange={(e) => onUpdate({ ...item, item_type: e.target.value })} className="h-8 text-xs border rounded-md px-2 w-24" data-testid="item-type-select">
          <option value="task">Task</option>
          <option value="document">Upload</option>
          <option value="signature">Signature</option>
          <option value="acknowledgment">Ack.</option>
        </select>
        <select value={item.assignee_role} onChange={(e) => onUpdate({ ...item, assignee_role: e.target.value })} className="h-8 text-xs border rounded-md px-2 w-28">
          <option value="New Hire">New Hire</option>
          <option value="HR">HR</option>
          <option value="Manager">Manager</option>
          <option value="IT">IT</option>
        </select>
        <label className="flex items-center gap-1 text-xs whitespace-nowrap">
          <input type="checkbox" checked={item.required} onChange={(e) => onUpdate({ ...item, required: e.target.checked })} />
          Req
        </label>
        <button onClick={() => setExpanded(v => !v)} className="text-muted-foreground hover:text-primary p-1" data-testid="item-details-toggle" title="Task details">
          <Settings2 size={15} />
        </button>
        <button onClick={onRemove} className="text-red-400 hover:text-red-600 p-1"><X size={14} /></button>
      </div>

      {expanded && (
        <div className="mt-2 pl-1 space-y-2 border-t pt-2">
          <div>
            <Label className="text-xs">Instructions / notes for this task</Label>
            <Textarea
              value={item.description || ''}
              onChange={(e) => onUpdate({ ...item, description: e.target.value })}
              placeholder="e.g. Upload a clear photo of the front of your driver's license."
              className="text-sm min-h-[60px]"
              data-testid="item-instructions-input"
            />
          </div>

          {item.item_type === 'signature' && (
            <div>
              <Label className="text-xs">Signature template to sign</Label>
              <select
                value={item.esign_template_id || ''}
                onChange={(e) => onUpdate({ ...item, esign_template_id: e.target.value || null })}
                className="h-8 text-sm border rounded-md px-2 w-full"
                data-testid="item-esign-template-select"
              >
                <option value="">— Select a template —</option>
                {esignTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}

          {item.item_type === 'document' && (
            <div>
              <Label className="text-xs">What should the new hire upload? (filed into Credentials)</Label>
              <select
                value={item.credential_type || ''}
                onChange={(e) => onUpdate({ ...item, credential_type: e.target.value || null })}
                className="h-8 text-sm border rounded-md px-2 w-full"
                data-testid="item-credential-type-select"
              >
                <option value="">Any document (auto-detect)</option>
                {credentialTypes.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}

          <div>
            <Label className="text-xs">Reference file (optional — new hire can download/view)</Label>
            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" onChange={uploadAttachment} className="hidden" data-testid="item-attachment-input" />
              <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}>
                <Paperclip size={13} className="mr-1" /> {uploading ? 'Uploading...' : 'Attach file'}
              </Button>
              {item.attachment?.file_name && (
                <span className="text-xs text-blue-600 flex items-center gap-1">
                  {item.attachment.file_name}
                  <button onClick={() => onUpdate({ ...item, attachment: null })} className="text-red-400 hover:text-red-600"><X size={12} /></button>
                </span>
              )}
            </div>
          </div>

          <div className="border-t pt-2">
            <Label className="text-xs">Due date</Label>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <select
                value={item.due_mode || 'none'}
                onChange={(e) => onUpdate({ ...item, due_mode: e.target.value })}
                className="h-8 text-sm border rounded-md px-2"
                data-testid="item-due-mode-select"
              >
                <option value="none">No due date</option>
                <option value="fixed">Fixed date</option>
                <option value="before_hire">Days before hire date</option>
                <option value="after_hire">Days after hire date</option>
              </select>
              {item.due_mode === 'fixed' && (
                <Input type="date" value={item.due_date || ''} onChange={(e) => onUpdate({ ...item, due_date: e.target.value })} className="h-8 text-sm w-40" data-testid="item-due-date-input" />
              )}
              {(item.due_mode === 'before_hire' || item.due_mode === 'after_hire') && (
                <span className="flex items-center gap-1 text-sm">
                  <Input type="number" min="0" value={item.due_offset_days ?? ''} onChange={(e) => onUpdate({ ...item, due_offset_days: e.target.value === '' ? null : parseInt(e.target.value, 10) })} className="h-8 text-sm w-20" data-testid="item-due-offset-input" />
                  days {item.due_mode === 'before_hire' ? 'before' : 'after'} hire
                </span>
              )}
            </div>
            {item.due_mode !== 'none' && (
              <div className="flex items-center gap-2 mt-2 text-sm">
                <Label className="text-xs">Email reminder</Label>
                <Input type="number" min="0" placeholder="off" value={item.reminder_days_before ?? ''} onChange={(e) => onUpdate({ ...item, reminder_days_before: e.target.value === '' ? null : parseInt(e.target.value, 10) })} className="h-8 text-sm w-20" data-testid="item-reminder-input" />
                <span className="text-xs text-muted-foreground">days before due (leave blank = no reminder)</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PackEditor({ pack, onSave, onCancel, esignTemplates, credentialTypes, headers }) {
  const [form, setForm] = useState({
    name: pack?.name || '',
    description: pack?.description || '',
    industry: pack?.industry || 'Home Health',
    role: pack?.role || 'General',
    items: pack?.items || [],
  });

  const addItem = () => {
    setForm(f => ({
      ...f,
      items: [...f.items, { name: '', item_type: 'task', required: true, assignee_role: 'New Hire', description: '', due_mode: 'none', due_date: '', due_offset_days: 3, reminder_days_before: '' }],
    }));
  };

  const updateItem = (idx, updated) => {
    setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? updated : it) }));
  };

  const removeItem = (idx) => {
    setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = () => {
    if (!form.name.trim()) { toast.error('Pack name is required'); return; }
    if (form.items.some(it => !it.name.trim())) { toast.error('All items must have a name'); return; }
    const items = form.items.map(it => ({
      ...it,
      due_mode: it.due_mode || 'none',
      due_date: it.due_mode === 'fixed' ? (it.due_date || null) : null,
      due_offset_days: (it.due_mode === 'before_hire' || it.due_mode === 'after_hire')
        ? (it.due_offset_days === '' || it.due_offset_days == null ? null : parseInt(it.due_offset_days, 10)) : null,
      reminder_days_before: it.reminder_days_before === '' || it.reminder_days_before == null ? null : parseInt(it.reminder_days_before, 10),
    }));
    onSave({ ...form, items });
  };

  return (
    <Card className="p-6 border-primary/30 border-2" data-testid="pack-editor">
      <div className="flex justify-between items-start mb-4">
        <h3 className="font-semibold">{pack ? 'Edit Pack' : 'New Template Pack'}</h3>
        <button onClick={onCancel} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <Label>Pack Name</Label>
          <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Home Health Therapist" data-testid="pack-name-input" />
        </div>
        <div>
          <Label>Description</Label>
          <Input value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Brief description..." />
        </div>
        <div>
          <Label>Industry</Label>
          <Input value={form.industry} onChange={(e) => setForm(f => ({ ...f, industry: e.target.value }))} />
        </div>
        <div>
          <Label>Role</Label>
          <Input value={form.role} onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))} />
        </div>
      </div>
      <div className="mb-3 flex items-center justify-between">
        <Label>Checklist Items ({form.items.length})</Label>
        <Button size="sm" variant="outline" onClick={addItem} data-testid="add-pack-item-btn"><Plus size={14} className="mr-1" /> Add Item</Button>
      </div>
      <div className="space-y-1 max-h-80 overflow-y-auto mb-4">
        {form.items.map((item, idx) => (
          <PackItemRow key={idx} item={item} onUpdate={(u) => updateItem(idx, u)} onRemove={() => removeItem(idx)} esignTemplates={esignTemplates} credentialTypes={credentialTypes} headers={headers} />
        ))}
        {form.items.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No items yet. Click "Add Item" to start building your checklist.</p>}
      </div>
      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSubmit} data-testid="save-pack-btn">{pack ? 'Update Pack' : 'Create Pack'}</Button>
      </div>
    </Card>
  );
}

export default function OnboardingGallery() {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | pack object
  const [expandedPack, setExpandedPack] = useState(null);
  const [search, setSearch] = useState('');
  const [esignTemplates, setEsignTemplates] = useState([]);
  const [credentialTypes, setCredentialTypes] = useState([]);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchPacks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/onboarding/packs`, { headers });
      setPacks(res.data);
    } catch { toast.error('Failed to load packs'); }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPacks();
    axios.get(`${API}/esignature/templates`, { headers }).then(r => setEsignTemplates(r.data || [])).catch(() => {});
    axios.get(`${API}/credentials/types`, { headers }).then(r => setCredentialTypes(r.data?.types || [])).catch(() => {});
  }, [fetchPacks]);

  const seedDefaults = async () => {
    try {
      const res = await axios.post(`${API}/onboarding/packs/seed`, {}, { headers });
      toast.success(res.data.message);
      fetchPacks();
    } catch { toast.error('Failed to seed packs'); }
  };

  const savePack = async (form) => {
    try {
      if (editing && editing !== 'new' && editing.id) {
        await axios.put(`${API}/onboarding/packs/${editing.id}`, form, { headers });
        toast.success('Pack updated');
      } else {
        await axios.post(`${API}/onboarding/packs`, form, { headers });
        toast.success('Pack created');
      }
      setEditing(null);
      fetchPacks();
    } catch { toast.error('Failed to save pack'); }
  };

  const deletePack = async (packId) => {
    if (!window.confirm('Archive this template pack?')) return;
    try {
      await axios.delete(`${API}/onboarding/packs/${packId}`, { headers });
      toast.success('Pack archived');
      fetchPacks();
    } catch { toast.error('Failed to archive pack'); }
  };

  const duplicatePack = async (pack) => {
    const copy = {
      name: `${pack.name} (Copy)`,
      description: pack.description,
      industry: pack.industry,
      role: pack.role,
      items: pack.items,
    };
    try {
      await axios.post(`${API}/onboarding/packs`, copy, { headers });
      toast.success('Pack duplicated');
      fetchPacks();
    } catch { toast.error('Failed to duplicate pack'); }
  };

  const filtered = packs.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.industry?.toLowerCase().includes(search.toLowerCase()) ||
    p.role?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="onboarding-gallery-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Package size={24} /> Onboarding Template Gallery</h1>
          <p className="text-muted-foreground text-sm mt-1">Pre-built onboarding checklists by industry and role. Customize and assign to new hires.</p>
        </div>
        <div className="flex gap-2">
          {packs.length === 0 && (
            <Button variant="outline" onClick={seedDefaults} data-testid="seed-packs-btn"><Package size={16} className="mr-1" /> Load Defaults</Button>
          )}
          <Button onClick={() => setEditing('new')} data-testid="create-pack-btn"><Plus size={16} className="mr-1" /> New Pack</Button>
        </div>
      </div>

      {editing && (
        <PackEditor
          pack={editing !== 'new' ? editing : null}
          onSave={savePack}
          onCancel={() => setEditing(null)}
          esignTemplates={esignTemplates}
          credentialTypes={credentialTypes}
          headers={headers}
        />
      )}

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search packs by name, industry, or role..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="pack-search-input"
        />
      </div>

      <div className="grid gap-4">
        {filtered.map((pack) => {
          const IndustryIcon = INDUSTRY_ICONS[pack.industry] || INDUSTRY_ICONS['default'];
          const isExpanded = expandedPack === pack.id;
          const roleBreakdown = {};
          (pack.items || []).forEach(it => {
            roleBreakdown[it.assignee_role] = (roleBreakdown[it.assignee_role] || 0) + 1;
          });

          return (
            <Card key={pack.id} className="overflow-hidden" data-testid={`pack-card-${pack.id}`}>
              <div
                className="p-5 cursor-pointer hover:bg-muted/30 transition-colors"
                onClick={() => setExpandedPack(isExpanded ? null : pack.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <IndustryIcon size={20} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-base">{pack.name}</h3>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge className="text-[10px] bg-muted text-muted-foreground">{pack.industry}</Badge>
                        <Badge className="text-[10px] bg-muted text-muted-foreground">{pack.role}</Badge>
                        <span className="text-xs text-muted-foreground">{(pack.items || []).length} items</span>
                        {pack.is_default && <Badge className="text-[10px] bg-blue-50 text-blue-600">Default</Badge>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1 mr-2">
                      {Object.entries(roleBreakdown).map(([role, count]) => (
                        <Badge key={role} className={`text-[10px] px-1.5 ${ROLE_COLORS[role] || 'bg-gray-100 text-gray-700'}`}>
                          {role}: {count}
                        </Badge>
                      ))}
                    </div>
                    {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </div>
                </div>
                {pack.description && <p className="text-sm text-muted-foreground mt-2 ml-[52px]">{pack.description}</p>}
              </div>

              {isExpanded && (
                <div className="border-t px-5 py-4 bg-muted/10">
                  <div className="space-y-1.5 mb-4">
                    {(pack.items || []).map((item, idx) => (
                      <PackItemRow key={idx} item={item} readOnly esignTemplates={esignTemplates} credentialTypes={credentialTypes} />
                    ))}
                  </div>
                  <div className="flex gap-2 justify-end border-t pt-3">
                    <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); duplicatePack(pack); }}><Copy size={14} className="mr-1" /> Duplicate</Button>
                    <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setEditing(pack); }}><Pencil size={14} className="mr-1" /> Edit</Button>
                    <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={(e) => { e.stopPropagation(); deletePack(pack.id); }}><Trash2 size={14} className="mr-1" /> Archive</Button>
                  </div>
                </div>
              )}
            </Card>
          );
        })}

        {filtered.length === 0 && !editing && (
          <Card className="p-8 text-center">
            <Package size={32} className="mx-auto mb-3 text-muted-foreground/40" />
            <h3 className="font-medium mb-1">No Template Packs Found</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {packs.length === 0 ? 'Load industry defaults or create your first custom pack.' : 'No packs match your search.'}
            </p>
            {packs.length === 0 && (
              <div className="flex gap-2 justify-center">
                <Button variant="outline" onClick={seedDefaults}>Load Defaults</Button>
                <Button onClick={() => setEditing('new')}>Create Custom</Button>
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
